import { ChevronRight } from "lucide-react";
import { Fragment } from "react";

import {
  DATABOOK_BUILDER_STAGES,
  type BuilderStageNavigationItem,
} from "./builderStages";

interface BuilderStageNavigationProps {
  activeStage: string;
  questionsBadge?: number;
  onStageSelect: (stage: string) => void;
  stages?: readonly BuilderStageNavigationItem[];
  loading?: boolean;
  loadFailed?: boolean;
  fullWidth?: boolean;
}

export function BuilderStageNavigation({
  activeStage,
  questionsBadge,
  onStageSelect,
  stages = DATABOOK_BUILDER_STAGES,
  loading = false,
  loadFailed = false,
  fullWidth = false,
}: BuilderStageNavigationProps) {
  if (loading || loadFailed) {
    return (
      <nav
        aria-label="Databook Builder progress"
        aria-busy={loading && !loadFailed}
        className="flex h-[26px] items-center"
      >
        {loadFailed ? (
          <span role="status" className="text-xs text-muted-foreground">
            Navigation unavailable. Refresh the page.
          </span>
        ) : (
          <>
            <span className="sr-only">Loading navigation…</span>
            <span aria-hidden="true" className="h-4 w-64 max-w-full animate-pulse rounded bg-muted" />
          </>
        )}
      </nav>
    );
  }

  return (
    <nav aria-label="Databook Builder progress" className={`flex min-h-[26px] items-center gap-1 ${fullWidth ? "w-full justify-between whitespace-nowrap" : ""}`}>
      {stages.map((stage, index) => {
        const active = activeStage === stage.key;
        const badge = stage.key === "questions" ? questionsBadge : undefined;

        return (
          <Fragment key={stage.key}>
            {index > 0 && (
              <ChevronRight
                aria-hidden="true"
                size={11}
                className="text-muted-foreground/50"
              />
            )}
            <button
              type="button"
              aria-current={active ? "step" : undefined}
              onClick={() => onStageSelect(stage.key)}
              className={`rounded-lg pl-1.5 pr-2.5 py-1 font-medium flex items-center gap-1.5 ${
                active
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-muted"
              }`}
              style={{ fontSize: 11.5 }}
            >
              <span
                className="inline-flex items-center justify-center rounded-full border"
                style={{
                  width: 15,
                  height: 15,
                  fontSize: 9,
                  borderColor: active
                    ? "hsl(var(--background) / 0.5)"
                    : "hsl(var(--border))",
                }}
              >
                {index + 1}
              </span>
              {stage.label}
              {!!badge && (
                <span
                  className={`rounded-full px-1.5 ${
                    active
                      ? "bg-card text-foreground"
                      : "bg-muted text-foreground/80"
                  }`}
                  style={{ fontSize: 9.5 }}
                >
                  {badge}
                </span>
              )}
            </button>
          </Fragment>
        );
      })}
    </nav>
  );
}
